import { ATHLETE_TIME_ZONES, type AthleteTimeZone } from "./athleteTimeZones.js";

// #510, 30/10: the device's time zone becomes a PROPOSAL the athlete confirms,
// never a value saved on its own (Antenore: "Proposto dal telefono, lo
// confermi tu"). Only the zone the system declares, no position, no IP.
//
// A device id maps to one of the listed regions only when it keeps the SAME
// UTC offset on four probe days spread over the year: mid-January, April,
// July and October catch winter time, summer time, and the southern
// hemisphere's inverted seasons. Any doubt proposes nothing: an unknown id, an
// engine without the Intl this needs, or no region with the same offsets.
//
// ⚠️ Two pairs of regions share their offsets all year (Istanbul and Moscow at
// +3, Shanghai and Perth at +8). Intl gives no longitude for an id, so a tie
// goes to the first region in list order (west to east). The day counted is
// the same either way; only the label proposed can be the neighbour's.

// Noon UTC, 15th of the month, 2026: away from every changeover date.
const PROBES_MS = [0, 3, 6, 9].map((month) => Date.UTC(2026, month, 15, 12));

function offsetSignature(timeZone: string): string {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
  return PROBES_MS.map((ms) => {
    const parts = fmt.formatToParts(ms);
    const n = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((p) => p.type === type)?.value);
    // `% 24`: some engines still write midnight as 24 under h23.
    const local = Date.UTC(n("year"), n("month") - 1, n("day"), n("hour") % 24, n("minute"));
    const minutes = Math.round((local - ms) / 60_000);
    if (!Number.isFinite(minutes)) throw new RangeError(`no offset for ${timeZone}`);
    return minutes;
  }).join(",");
}

let listed: ReadonlyArray<{ id: AthleteTimeZone; signature: string }> | null = null;

/** The listed region proposed for a device time zone, or null when none fits
 *  exactly. The id itself when listed; else the first region with the same
 *  UTC offset on the four probe days. */
export function proposeTimeZoneRegion(deviceTimeZone: string | undefined): AthleteTimeZone | null {
  if (deviceTimeZone === undefined || deviceTimeZone === "") return null;
  const same = ATHLETE_TIME_ZONES.find((z) => z.id === deviceTimeZone);
  if (same) return same.id;
  try {
    listed ??= ATHLETE_TIME_ZONES.map((z) => ({ id: z.id, signature: offsetSignature(z.id) }));
    const signature = offsetSignature(deviceTimeZone);
    return listed.find((z) => z.signature === signature)?.id ?? null;
  } catch {
    return null;
  }
}

/** The IANA zone the system declares, or undefined when Intl cannot say
 *  (missing, stripped, or no `timeZone`): then nothing is proposed. */
export function deviceTimeZone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === "string" && zone !== "" ? zone : undefined;
  } catch {
    return undefined;
  }
}
